import { type AnyAction } from 'redux';
import { store, reducerService } from 'js/store';
import { createDeepEqualSelector } from 'js/utils/redux.util';
import React, { useCallback, useLayoutEffect, useRef } from 'react';
import { useDispatch, useSelector, type TypedUseSelectorHook } from 'react-redux';

import * as Types from './types';
import * as helpers from './helpers';

export const withDynamicReducer =
    <TState extends object, TAction extends AnyAction = AnyAction, TReducerName = string>(
        options: Types.IWithDynamicReducerOptions<TState, TAction, TReducerName>
    ) =>
    <P extends object>(WrappedComponent: React.ComponentType<P>) =>
    (props: React.PropsWithChildren<P>) => {
        const { reducerName, Context, reducer, defaultState, initialState } = options;
        const dispatch = useDispatch();
        const uniqueId = useRef<string>('');
        const reducerNameWithPrefix = useRef<string>('');

        useLayoutEffect(() => {
            uniqueId.current = helpers.getReducerUniqueId();
            reducerNameWithPrefix.current = `${reducerName}_${uniqueId.current}`;

            store.replaceReducer(reducerService.add(reducerNameWithPrefix.current, reducer, uniqueId.current));

            return () => {
                store.replaceReducer(reducerService.remove(reducerNameWithPrefix.current));
            };
        }, [reducer, reducerName]);

        const componentDispatch = useCallback((action: TAction) => {
            dispatch(helpers.addReducerIdToAction(action, uniqueId.current));
        }, [dispatch]);

        const useComponentSelector: TypedUseSelectorHook<TState> = useCallback((selector, equalityFn) => {
                const selectorMemo = useCallback(
                    // todo: think about how to avoid using "any" here
                    (state: any) => {
                        const componentState = state[reducerNameWithPrefix.current] || defaultState || initialState || {};

                        // return typeof selector === 'function' ? selector(componentState) : componentState;

                        if (typeof selector === 'function') {
                            return selector(componentState);
                        }

                        return componentState;
                    },
                    [selector]
                );

                const deepSelector = createDeepEqualSelector([selectorMemo], (data) => data);

                return useSelector(deepSelector, equalityFn);
        }, [defaultState, initialState]);

        const getReducerName = useCallback(() => {
            return reducerNameWithPrefix.current;
        }, []);

        return (
            <Context.Provider
                value={{
                    getReducerName,
                    componentDispatch,
                    useComponentSelector
                }}>
                <WrappedComponent {...props as P} />
            </Context.Provider>
        );
    };
